import {
  forOwn,
  cloneDeep,
  mapValues,
  isUndefined,
  uniq,
} from 'lodash'

//import { getOnePartitioned } from './storage'


let schema, idDelimiter, blobContainer

// holds schema and idDelimiter after setting
export const _test = {}

export const schemaSetup = (_schema, _idDelimiter, _blobContainer) => {
  schema = _schema
  idDelimiter = _idDelimiter
  blobContainer = _blobContainer
  _test.schema = schema
  _test.idDelimiter = idDelimiter
  _test.blobContainer = blobContainer
}


// makes the schema's attribute's options explicit (destructively)
export const normalise = () => {
  const reservedNames = ['PartitionKey', 'RowKey', 'Timestamp', 'id', 'errors', '.metadata']
  const validTypes = ['number', 'string', 'boolean']

  const badSchema = (s) => {
    throw `invalid schema: ${s}`
  }

  forOwn(schema, (attr, name) => {
    // disallow reserved names
    if(reservedNames.indexOf(name) !== -1)
      badSchema(`uses reserved name '${name}'`)

    // coerce attribute to object; string-only implies type
    if(typeof schema[name] !== 'object') {
      schema[name] = { type: schema[name] }
      attr = schema[name]
    }

    // 'references' implies a string type
    if(attr.references) {
      attr.type = attr.type || 'string'

      if(! isUndefined(attr.many)) {
        if(typeof attr.many !== 'boolean') {
          if(typeof attr.many !== 'number')
            badSchema(`references-many type not a boolean or number`)
          if(attr.many < 2)
            badSchema(`references-many must be at least 2`)
          if(attr.many !== Math.floor(attr.many))
            badSchema(`references-many must be an integer`)
        }
      }
    }
    else if(! isUndefined(attr.many))
      badSchema(`references-many only applies to references`)

    if(attr.blobStorage) {
      attr.type = 'string'
      if(attr.references)
        badSchema(`references can't be of a blobStorage type`)
      if(! blobContainer)
        badSchema(`blobStorage field specified without setting blobContainer`)
    }

    // we must have a type by this point
    if(! attr.type)
      badSchema(`no type specified for ${name}`)
    if(! validTypes.includes(attr.type))
      badSchema(`invalid type for ${name}: ${attr.type}`)

    // coerce 'optional' to boolean
    attr.optional = !!attr.optional

    // if step is specified, make sure it's a positive number
    if(! isUndefined(attr.step)) {
      if(attr.type !== 'number')
        badSchema(`step specified for non-numeric attribute '${name}'`)
      if((typeof attr.step !== 'number') || (attr.step <= 0))
        badSchema(`step for ${name} must be a positive number`)
    }
  })
}

// ensures an entity's references to other entities exist
const checkReferences = (errors, attr, objAttr, name) => {
  if(!attr.references)
    return
  // we've already dealt with null-ness at this point
  if(!objAttr)
    return

  const ids = objAttr.split(idDelimiter)
  if(typeof attr.many === 'number') {
    if(ids.length > attr.many)
      errors.push(`too many ids given for '${name}' (saw ${ids.length}, max ${attr.many}`)
  }
  else if((attr.many !== true) && (ids.length > 1))
    errors.push(`multiple ids specified for single-id reference ${name}`)

  if(uniq(ids).length !== ids.length)
    errors.push(`duplicate ids given for '${name}'`)

  // TODO: make sure all ids exist. this will require making the entire thing async.
  // const promises = []
  // ids.forEach((id) => {
  //   const promise = new Promise((resolve) => {
  //     getOnePartitioned(attr.references, id, (qRes) => {
  //       if(qRes.errors)
  //         errors.push(`referenced id '${name}' on '${attr.references}' doesn't exist: ${id}`)
  //       resolve()
  //     })
  //   })
  //   promises.push(promise)
  // })
  // return Promise.all(promises)
}


// deal with defaults, nulls, numbers to be rounded etc.
export const coerce = (entity, creating) => {
  // schema must be normalised by this point

  const entityOriginal = cloneDeep(entity)
  const deleteAttrs = []

  forOwn(schema, (attr, name) => {
    // would do the below, but need consistent r/w access:
    //const entityAttr = entity[name]

    const setDefault = () => {
      entity[name] =
        (typeof attr.default === 'function')
        ? attr.default(entityOriginal, creating)
        : attr.default
    }

    // if this field is passed in null or an empty string, either delete or default it
    if((entity[name] === null) || (entity[name] === '')) {
      if(! attr.optional && ! isUndefined(attr.default))
        setDefault()
      else {
        // validate() deals with requests attempting to unset required
        // fields; just delete it from the object for now
        deleteAttrs.push(name)
        return
      }
    }

    // also default the field if it's required but completely unspecified
    if(! attr.optional && isUndefined(entity[name]))
      setDefault()

    // if the field is now set, perform further coercions
    if(! isUndefined(entity[name])) {
      // if step is specified, round numbers to it
      if((typeof entity[name] === 'number') && !isUndefined(attr.step)) {
        entity[name] = Math.round(entity[name] / attr.step) * attr.step
      }
    }
  })

  deleteAttrs.forEach((name) => delete entity[name])
}


// validates an entity against the schema
export const validate = (obj) => {
  const errors = []

  // schema must be normalised by this point
  forOwn(schema, (attr, name) => {
    const objAttr = obj[name]

    // presence
    if(! attr.optional && isUndefined(objAttr)) {
      errors.push(`required '${name}' not specified`)
      return
    }

    // type
    if(! isUndefined(objAttr)) {
      const actualType = typeof objAttr
      if(attr.type !== actualType) {
        errors.push(`incorrect type for '${name}': expected ${attr.type} but got ${actualType}`)
        return
      }

      // number
      if(actualType === 'number') {
        if(attr.min && (objAttr < attr.min))
          errors.push(`'${name}': ${objAttr} below minimum value of ${attr.min}`)
        else if(attr.max && (objAttr > attr.max))
          errors.push(`'${name}': ${objAttr} above maximum value of ${attr.max}`)
      }
      else if(actualType === 'string') {
        if(attr.min && (objAttr.length < attr.min))
          errors.push(`'${name}': ${objAttr} below minimum value of ${attr.min}`)
        else if(attr.max && (objAttr.length > attr.max))
          errors.push(`'${name}': ${objAttr} above maximum value of ${attr.max}`)
      }
    }

    // references
    checkReferences(errors, attr, objAttr, name)

    // custom
    if(attr.validate) {
      try {
        if(! attr.validate(objAttr))
          // if falsy, use a general message
          throw `validation failed`
      }
      catch(e) {
        errors.push(e.message)
      }
    }
  })

  // check all attributes on the entity are specified in the schema
  forOwn(obj, (attr, name) => {
    if(! schema[name])
      errors.push(`'${name}' not an attribute`)
  })

  return errors
}



// WEBPACK FOOTER //
// ./src/schema.js